import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Instagram, Facebook, Youtube, MessageCircle, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import '../styles/design-system.css';
import '../styles/scroll-animations.css';

/* ── Animated input wrapper ─────────────────────────────── */
const AnimatedInput = ({ label, required, children }) => {
  const [focused, setFocused] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <label className="body-small block mb-2" style={{ color: focused ? 'var(--accent-gold-400)' : 'var(--text-secondary)', transition: 'color 0.2s' }}>
        {label} {required && '*'}
      </label>
      <div
        onFocusCapture={() => setFocused(true)}
        onBlurCapture={() => setFocused(false)}
        style={{
          position: 'relative',
          borderRadius: 8,
          border: `1.5px solid ${focused ? 'var(--accent-gold-400)' : 'var(--border-input)'}`,
          boxShadow: focused ? '0 0 0 3px rgba(184,134,11,0.12)' : 'none',
          transition: 'border-color 0.25s, box-shadow 0.25s',
          background: 'var(--bg-card)',
        }}
      >
        {children}
      </div>
    </motion.div>
  );
};

const ContactSection = () => {
  const { contactData } = useLanguage();
  const sectionRef = useRef(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const rawBgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);
  const bgY = useSpring(rawBgY, { stiffness: 50, damping: 18 });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1600));
    toast.success('Mensagem enviada! Entraremos em contato em breve.');
    setSent(true);
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setIsSubmitting(false);
    setTimeout(() => setSent(false), 4000);
  };

  const socialIcons = { instagram: Instagram, facebook: Facebook, youtube: Youtube };

  const baseInput = {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    width: '100%',
    color: 'var(--text-primary)',
    fontFamily: 'Inter, sans-serif',
  };

  const contactCards = [
    { accent: 'pink',  icon: Mail,    label: 'E-mail',      value: contactData.email   },
    { accent: 'blue',  icon: Phone,   label: 'Telefone',     value: contactData.phone   },
    { accent: 'green', icon: MapPin,  label: 'Localização',  value: contactData.address },
  ];

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-20 md:py-32 relative overflow-hidden grain-overlay"
      style={{ background: 'var(--bg-page)' }}
    >
      {/* Parallax decorative blob */}
      <motion.div
        style={{
          y: bgY,
          position: 'absolute', bottom: '-15%', right: '-5%',
          width: 500, height: 500,
          borderRadius: '60% 40% 55% 45% / 50% 60% 40% 50%',
          background: 'var(--accent-gold-200)', opacity: 0.3,
          pointerEvents: 'none', zIndex: 0,
        }}
      />

      <div className="container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-16"
        >
          <span className="mono-text mb-4 block" style={{ color: 'var(--accent-gold-400)' }}>CONTATO</span>
          <h2 className="heading-hero mb-4">{contactData.title}</h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
            {contactData.subtitle}
          </p>
          <motion.div
            className="section-divider mt-8"
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.0 }}
            style={{ transformOrigin: 'center' }}
          />
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left — Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="space-y-4 mb-8">
              {contactCards.map(({ accent, icon: Icon, label, value }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ x: 6, boxShadow: '0 8px 30px rgba(139,115,85,0.12)' }}
                  className={`voice-card accent-${accent} flex items-center gap-4`}
                  style={{ transition: 'box-shadow 0.3s' }}
                >
                  <motion.div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(255,255,255,0.5)' }}
                    whileHover={{ rotate: 12, scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  >
                    <Icon size={20} style={{ color: 'var(--text-primary)' }} />
                  </motion.div>
                  <div>
                    <p className="caption mb-0.5">{label}</p>
                    <p className="body-medium">{value}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* WhatsApp CTA */}
            <motion.a
              href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}?text=Olá! Vim pelo site e gostaria de mais informações.`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-3 mb-8 rounded-xl py-4 font-medium"
              style={{
                background: '#25D366',
                color: '#fff',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontFamily: 'Inter, sans-serif',
                boxShadow: '0 4px 20px rgba(37,211,102,0.3)',
              }}
              whileHover={{ scale: 1.03, y: -3, boxShadow: '0 12px 30px rgba(37,211,102,0.4)' }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <motion.span
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
              >
                <MessageCircle size={20} />
              </motion.span>
              Fale pelo WhatsApp
            </motion.a>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <p className="body-small mb-4" style={{ color: 'var(--text-muted)' }}>Siga nas redes sociais</p>
              <div className="flex gap-3">
                {Object.entries(contactData.social).map(([platform, handle], i) => {
                  const IconComponent = socialIcons[platform];
                  return (
                    <motion.a
                      key={platform}
                      href={`https://${platform}.com/${handle.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-nav"
                      aria-label={platform}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 }}
                      whileHover={{ scale: 1.15, y: -3, rotate: 5 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <IconComponent size={18} />
                    </motion.a>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="voice-card" style={{ position: 'relative', overflow: 'hidden' }}>
              {/* Card shimmer top border */}
              <motion.div
                style={{ height: 3, background: 'linear-gradient(90deg, var(--accent-bronze-400), var(--accent-gold-400), var(--accent-bronze-400))', backgroundSize: '200% 100%', marginBottom: '1.5rem', borderRadius: 2 }}
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              />

              <h3 className="heading-3 mb-6">Envie uma mensagem</h3>

              <AnimatePresence mode="wait">
                {sent ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex flex-col items-center justify-center py-12 gap-4"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    >
                      <CheckCircle size={48} style={{ color: '#25D366' }} />
                    </motion.div>
                    <p className="heading-3 text-center">Mensagem enviada!</p>
                    <p className="body-small text-center" style={{ color: 'var(--text-muted)' }}>
                      Retornaremos em breve 🙏
                    </p>
                  </motion.div>
                ) : (
                  <motion.form key="form" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                      <AnimatedInput label="Nome completo" required>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} required style={baseInput} placeholder="Seu nome" />
                      </AnimatedInput>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <AnimatedInput label="E-mail" required>
                          <input type="email" name="email" value={formData.email} onChange={handleChange} required style={baseInput} placeholder="seu@email.com" />
                        </AnimatedInput>
                        <AnimatedInput label="Telefone" required>
                          <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required style={baseInput} placeholder="(00) 00000-0000" />
                        </AnimatedInput>
                      </div>

                      <AnimatedInput label="Assunto" required>
                        <select name="subject" value={formData.subject} onChange={handleChange} required style={{ ...baseInput, appearance: 'none' }}>
                          <option value="">Selecione um assunto</option>
                          {contactData.formFields.find(f => f.name === 'subject')?.options.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </AnimatedInput>

                      <AnimatedInput label="Mensagem" required>
                        <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} style={{ ...baseInput, resize: 'vertical', minHeight: 110 }} placeholder="Escreva sua mensagem..." />
                      </AnimatedInput>

                      <motion.button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                        style={{ opacity: isSubmitting ? 0.75 : 1, marginTop: '0.5rem' }}
                        whileHover={{ scale: isSubmitting ? 1 : 1.03, y: isSubmitting ? 0 : -2 }}
                        whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                      >
                        {isSubmitting ? (
                          <>
                            <motion.div
                              style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%' }}
                              animate={{ rotate: 360 }}
                              transition={{ repeat: Infinity, duration: 0.7, ease: 'linear' }}
                            />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            Enviar Mensagem
                          </>
                        )}
                      </motion.button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
